import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { useAuth } from "../contexts/AuthContext";
import CommentForm from "./CommentForm";
import CommentList from "./CommentList";
import {
  BlogMediaItem,
  AudioWithHeadingAndTranscript,
  BlogPost,
} from "../utils/mediaHelpers";
import TranscribeButton from "./TranscribeButton";

const BlogDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    type: "photos" | "videos" | "pdfs" | "audios" | "blog" | null;
    idx: number | null;
  }>({ type: null, idx: null });
  const [editValue, setEditValue] = useState<string>("");
  const [editTranscript, setEditTranscript] = useState<string>("");
  const [editContent, setEditContent] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [insertType, setInsertType] = useState<
    null | "photos" | "videos" | "pdfs" | "audios"
  >(null);
  const [insertFields, setInsertFields] = useState({
    url: "",
    heading: "",
    transcript: "",
    file: null as File | null,
  });
  const [insertIndex, setInsertIndex] = useState<number | null>(null);
  const [insertMenuAnchor, setInsertMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [mediaInsertMenuAnchor, setMediaInsertMenuAnchor] = useState<{
    [idx: number]: HTMLElement | null;
  }>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({ open: false, message: "", severity: "success" });

  const fetchPost = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/blog/${id}`, {
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
      });
      if (!res.ok) throw new Error("Failed to fetch blog post");
      const data = await res.json();

      console.log("Raw blog data:", data); // Debug log

      // Handle new backend format with media field
      const convertedData: BlogPost = {
        id: data.id,
        title: data.title,
        content: data.content,
        created_at: data.created_at,
        updated_at: data.updated_at,
        media: [],
      };

      // Check if data has the new media format
      if (data.media && Array.isArray(data.media)) {
        // New format: data.media is already an array of BlogMediaItem objects
        convertedData.media = data.media;
        console.log("Using new media format:", data.media); // Debug log
      } else {
        // Fallback to old format conversion
        console.log("Using old format conversion"); // Debug log
        // Convert photos
        if (data.photos && Array.isArray(data.photos)) {
          data.photos.forEach((photo: any) => {
            convertedData.media.push({
              type: "photo",
              data: {
                url: photo.url,
                heading: photo.caption,
              },
            });
          });
        }

        // Convert videos
        if (data.videos && Array.isArray(data.videos)) {
          data.videos.forEach((video: any) => {
            convertedData.media.push({
              type: "video",
              data: {
                url: video.url,
                heading: video.caption,
              },
            });
          });
        }

        // Convert PDFs
        if (data.pdfs && Array.isArray(data.pdfs)) {
          data.pdfs.forEach((pdf: any) => {
            convertedData.media.push({
              type: "pdf",
              data: {
                url: pdf.url,
                heading: pdf.caption,
              },
            });
          });
        }

        // Convert audios
        if (data.audios && Array.isArray(data.audios)) {
          data.audios.forEach((audio: any) => {
            convertedData.media.push({
              type: "audio",
              data: {
                url: audio.url,
                heading: audio.caption,
                transcript: audio.transcript,
              },
            });
          });
        }
      }

      console.log("Final converted data:", convertedData); // Debug log
      setPost(convertedData);
    } catch (e: any) {
      setError(e.message || "Error loading blog post");
    } finally {
      setLoading(false);
    }
  }, [id, user?.token]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const handleEdit = (
    type: "photos" | "videos" | "pdfs" | "audios" | "blog",
    idx: number
  ) => {
    setEditing({ type, idx });
    if (!post) return;
    if (type === "audios") {
      const audioData = post.media[idx].data as AudioWithHeadingAndTranscript;
      setEditValue(audioData.heading || "");
      setEditTranscript(audioData.transcript || "");
    } else if (type === "blog") {
      setEditValue(post.title);
      setEditContent(post.content);
    } else {
      setEditValue((post.media[idx].data as any).heading || "");
    }
  };

  const handleCancel = () => {
    setEditing({ type: null, idx: null });
    setEditValue("");
    setEditTranscript("");
    setEditContent("");
  };

  const handleSave = async () => {
    if (!post || editing.type === null || editing.idx === null) return;
    const idx = editing.idx;
    setSaving(true);
    try {
      const updatedMedia = post.media.map((item, i) => {
        if (i !== idx) return item;
        if (editing.type === "audios") {
          const audioData = item.data as AudioWithHeadingAndTranscript;
          return {
            ...item,
            data: {
              ...audioData,
              heading: editValue,
              transcript: editTranscript,
            },
          };
        } else if (editing.type === "blog") {
          // Handle blog title/content edit
          return item;
        } else {
          return {
            ...item,
            data: {
              ...item.data,
              heading: editValue,
            },
          };
        }
      });

      // Update local state immediately
      setPost((prev) => {
        if (!prev) return prev;
        return { ...prev, media: updatedMedia };
      });

      // Save to backend using new BlogPost format
      const res = await fetch(`/api/blog/${post.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify({
          title: post.title,
          content: post.content,
          media: updatedMedia,
          hidden: false,
        }),
      });

      if (res.ok) {
        showSnackbar("Media updated successfully!", "success");
        // Keep local state to preserve media order - don't refresh from backend
      } else {
        showSnackbar("Failed to update media", "error");
        // Revert local state if save failed
        setPost((prev) => {
          if (!prev) return prev;
          return { ...prev, media: post.media };
        });
      }

      setEditing({ type: null, idx: null });
      setEditValue("");
      setEditTranscript("");
      setEditContent("");
    } catch (err) {
      console.error("Error updating media:", err);
      showSnackbar("Failed to update media", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBlog = async () => {
    if (!post) return;
    setSaving(true);
    try {
      const updatedPost = {
        ...post,
        title: editValue,
        content: editContent,
      };

      // Update local state immediately
      setPost(updatedPost);

      // Save to backend
      const res = await fetch(`/api/blog/${post.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify({
          title: editValue,
          content: editContent,
          media: post.media,
          hidden: false,
        }),
      });

      if (res.ok) {
        showSnackbar("Blog post updated successfully!", "success");
        // Keep local state to preserve media order - don't refresh from backend
      } else {
        showSnackbar("Failed to update blog post", "error");
        // Revert local state if save failed
        setPost(post);
      }

      setEditing({ type: null, idx: null });
      setEditValue("");
      setEditTranscript("");
      setEditContent("");
    } catch (err) {
      console.error("Error updating blog post:", err);
      showSnackbar("Failed to update blog post", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlog = async () => {
    if (!post) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/blog/${post.id}`, {
        method: "DELETE",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
      });
      if (res.ok) {
        showSnackbar("Blog post deleted successfully!", "success");
        navigate("/blog");
      } else {
        showSnackbar("Failed to delete blog post", "error");
      }
    } catch (err) {
      console.error("Error deleting blog post:", err);
      showSnackbar("Failed to delete blog post", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteMedia = async (idx: number) => {
    if (!post) return;
    try {
      const updatedMedia = post.media.filter((_, i) => i !== idx);

      // Update local state immediately
      setPost((prev) => {
        if (!prev) return prev;
        return { ...prev, media: updatedMedia };
      });

      // Save to backend
      const res = await fetch(`/api/blog/${post.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify({
          title: post.title,
          content: post.content,
          media: updatedMedia,
          hidden: false,
        }),
      });

      if (res.ok) {
        showSnackbar("Media deleted successfully!", "success");
        // Keep local state to preserve media order - don't refresh from backend
      } else {
        showSnackbar("Failed to delete media", "error");
        // Revert local state if save failed
        setPost((prev) => {
          if (!prev) return prev;
          return { ...prev, media: post.media };
        });
      }
    } catch (err) {
      console.error("Error deleting media:", err);
      showSnackbar("Failed to delete media", "error");
    }
  };

  const handleInsertFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    if (name === "file" && files) {
      setInsertFields((prev) => ({ ...prev, file: files[0] }));
    } else {
      setInsertFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const showSnackbar = (
    message: string,
    severity: "success" | "info" | "warning" | "error" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleInsertSubmit = async () => {
    if (!insertType || !post) return;
    let mediaUrl = insertFields.url;
    if (insertFields.file) {
      const formData = new FormData();
      formData.append("file", insertFields.file);

      // Use the correct endpoint based on file type
      let endpoint = "/api/upload";
      if (insertType === "photos") {
        endpoint = "/api/upload/image";
      } else if (insertType === "videos") {
        endpoint = "/api/upload/video";
      } else if (insertType === "pdfs") {
        endpoint = "/api/upload/pdf";
      } else if (insertType === "audios") {
        endpoint = "/api/upload/audio";
        formData.append("heading", insertFields.heading);
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        mediaUrl = data.url || data.image_url;
        showSnackbar("Upload successful!", "success");
      } else {
        const errorData = await res.json().catch(() => ({}));
        const errorMessage = errorData.detail || "Upload failed";
        showSnackbar(`Upload failed: ${errorMessage}`, "error");
        return;
      }
    }
    let newItem: BlogMediaItem;
    if (insertType === "audios") {
      newItem = {
        type: "audio",
        data: {
          url: mediaUrl,
          heading: insertFields.heading,
          transcript: insertFields.transcript,
        },
      };
    } else {
      newItem = {
        type:
          insertType === "photos"
            ? "photo"
            : insertType === "videos"
            ? "video"
            : "pdf",
        data: {
          url: mediaUrl,
          heading: insertFields.heading,
        },
      };
    }

    // Create updated media array with new item
    const updatedMedia = [...post.media];
    const idx = insertIndex !== null ? insertIndex : updatedMedia.length;
    updatedMedia.splice(idx, 0, newItem);

    // Update local state immediately
    setPost((prev) => {
      if (!prev) return prev;
      return { ...prev, media: updatedMedia };
    });

    // Save to backend using new BlogPost format
    const res = await fetch(`/api/blog/${post.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
      },
      body: JSON.stringify({
        title: post.title,
        content: post.content,
        media: updatedMedia,
        hidden: false,
      }),
    });

    if (res.ok) {
      showSnackbar("Media inserted successfully!", "success");
      // Keep local state to preserve insert position - don't refresh from backend
    } else {
      showSnackbar("Failed to insert media", "error");
      // Revert local state if save failed
      setPost((prev) => {
        if (!prev) return prev;
        return { ...prev, media: post.media };
      });
    }

    setInsertType(null);
    setInsertFields({ url: "", heading: "", transcript: "", file: null });
    setInsertIndex(null);
  };

  const renderMedia = (item: BlogMediaItem, idx: number) => {
    console.log("Rendering media item:", item, "at index:", idx); // Debug log
    const isEditing = editing.type !== null && editing.idx === idx;
    const renderEditForm = () => (
      <Box sx={{ mb: 2 }}>
        <TextField
          margin="dense"
          label="Heading"
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
        />
        {editing.type === "audios" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              margin="dense"
              label="Transcript"
              fullWidth
              multiline
              rows={4}
              value={editTranscript}
              onChange={(e) => setEditTranscript(e.target.value)}
            />
            {post && (
              <TranscribeButton
                postId={post.id}
                mediaIndex={editing.idx ?? 0}
                userToken={user?.token}
                onTranscript={(transcript, updated) => {
                  setEditTranscript(transcript);
                  if (updated) setPost(updated);
                }}
                disabled={saving}
              />
            )}
          </Box>
        )}
        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            Save
          </Button>
          <Button onClick={handleCancel} variant="outlined" disabled={saving}>
            Cancel
          </Button>
        </Box>
      </Box>
    );

    const renderMediaContent = () => {
      console.log("Rendering media content for item:", item); // Debug log
      switch (item.type) {
        case "photo":
          console.log("Rendering photo with URL:", item.data.url); // Debug log
          return (
            <Box key={idx} sx={{ mb: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "#f0f0f0",
                  borderRadius: "4px",
                  minHeight: 120,
                  overflow: "hidden",
                }}
              >
                <img
                  src={item.data.url}
                  alt={item.data.heading || "Photo"}
                  loading="lazy"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "4px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    display: "block",
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector("[data-missing-media]")) {
                      const placeholder = document.createElement("div");
                      placeholder.setAttribute("data-missing-media", "1");
                      placeholder.textContent =
                        "File missing on server — re-upload this image";
                      placeholder.style.cssText =
                        "padding:48px 16px;text-align:center;color:#666;font-size:14px;";
                      parent.appendChild(placeholder);
                    }
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, fontWeight: "bold" }}
              >
                {item.data.heading}
              </Typography>
            </Box>
          );
        case "video":
          return (
            <Box key={idx} sx={{ mb: 2 }}>
              <video src={item.data.url} controls style={{ maxWidth: 400 }} />
              <Typography variant="caption">{item.data.heading}</Typography>
            </Box>
          );
        case "pdf":
          return (
            <Box key={idx} sx={{ mb: 2 }}>
              <a href={item.data.url} target="_blank" rel="noopener noreferrer">
                <DescriptionIcon /> {item.data.heading}
              </a>
            </Box>
          );
        case "audio":
          const audioData = item.data as AudioWithHeadingAndTranscript;
          return (
            <Box key={idx} sx={{ mb: 2 }}>
              <audio src={item.data.url} controls style={{ width: 300 }} />
              <Typography variant="caption">{item.data.heading}</Typography>
              {"transcript" in audioData && audioData.transcript && (
                <Typography variant="body2" color="text.secondary">
                  {audioData.transcript}
                </Typography>
              )}
            </Box>
          );
        default:
          return null;
      }
    };

    return (
      <Box key={idx}>
        {isEditing ? (
          renderEditForm()
        ) : (
          <>
            {renderMediaContent()}
            {user && (user.role === "admin" || user.role === "blog") && (
              <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    handleEdit(
                      item.type === "audio"
                        ? "audios"
                        : (`${item.type}s` as any),
                      idx
                    )
                  }
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={(e) => {
                    setMediaInsertMenuAnchor((prev) => ({
                      ...prev,
                      [idx]: e.currentTarget,
                    }));
                    setInsertIndex(idx);
                  }}
                >
                  Insert
                </Button>
                <Menu
                  anchorEl={mediaInsertMenuAnchor[idx]}
                  open={Boolean(mediaInsertMenuAnchor[idx])}
                  onClose={() =>
                    setMediaInsertMenuAnchor((prev) => ({
                      ...prev,
                      [idx]: null,
                    }))
                  }
                >
                  <MenuItem
                    onClick={() => {
                      setInsertType("photos");
                      setInsertIndex(idx);
                      setMediaInsertMenuAnchor((prev) => ({
                        ...prev,
                        [idx]: null,
                      }));
                    }}
                  >
                    Photo
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setInsertType("videos");
                      setInsertIndex(idx);
                      setMediaInsertMenuAnchor((prev) => ({
                        ...prev,
                        [idx]: null,
                      }));
                    }}
                  >
                    Video
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setInsertType("pdfs");
                      setInsertIndex(idx);
                      setMediaInsertMenuAnchor((prev) => ({
                        ...prev,
                        [idx]: null,
                      }));
                    }}
                  >
                    PDF
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setInsertType("audios");
                      setInsertIndex(idx);
                      setMediaInsertMenuAnchor((prev) => ({
                        ...prev,
                        [idx]: null,
                      }));
                    }}
                  >
                    Audio
                  </MenuItem>
                </Menu>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleDeleteMedia(idx)}
                >
                  Delete
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  // A single typed newline is a line break. Markdown otherwise folds it into the same line.
  // List items and blank lines are left alone so paragraphs and numbered lists still work.
  const keepTypedLineBreaks = (text: string) =>
    text
      .split("\n")
      .map((line, index, lines) => {
        const next = lines[index + 1];
        if (next === undefined || line.trim() === "" || next.trim() === "") {
          return line;
        }
        const blockStart = /^\s*(?:[-*+]|\d+\.)\s|^\s*#{1,6}\s|^\s*>/;
        if (blockStart.test(line) || blockStart.test(next)) {
          return line;
        }
        return `${line}  `;
      })
      .join("\n");

  const renderContentWithMedia = (content: string, media: BlogMediaItem[]) => {
    // Split by [media:N] marker, keeping the marker in the result
    const parts = content.split(/(\[media:(\d+)\])/g);
    return parts.map((part, idx) => {
      const match = part.match(/^\[media:(\d+)\]$/);
      if (match) {
        const mediaIdx = parseInt(match[1], 10);
        const item = media[mediaIdx];
        if (!item) {
          // Show a placeholder for missing media items
          return (
            <Box
              key={idx}
              sx={{
                mb: 2,
                p: 2,
                border: "1px dashed #ccc",
                borderRadius: 1,
                backgroundColor: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                📎 Media item {mediaIdx} not found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This media item may have been deleted or not yet uploaded.
              </Typography>
            </Box>
          );
        }
        // Reuse your renderMedia function for consistency
        return (
          <React.Fragment key={idx}>
            {renderMedia(item, mediaIdx)}
          </React.Fragment>
        );
      }
      // Otherwise, render the text as markdown (for chat formatting)
      return (
        <Box key={idx} sx={{ mb: 1 }}>
          <ReactMarkdown
            components={{
              // Custom styling for chat elements
              h2: ({ children }) => (
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1976d2",
                    fontWeight: "bold",
                    mt: 2,
                    mb: 1,
                    borderBottom: "1px solid #e0e0e0",
                    pb: 1,
                  }}
                >
                  {children}
                </Typography>
              ),
              strong: ({ children }) => (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: "bold",
                    color: "#d32f2f",
                    backgroundColor: "#f5f5f5",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    mr: 1,
                  }}
                >
                  {children}
                </Typography>
              ),
              p: ({ children }) => (
                <Typography
                  component="p"
                  sx={{ mb: 2, whiteSpace: "pre-wrap" }}
                >
                  {children}
                </Typography>
              ),
            }}
          >
            {keepTypedLineBreaks(part)}
          </ReactMarkdown>
        </Box>
      );
    });
  };

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  if (!post) return null;

  return (
    <Box maxWidth="md" mx="auto" mt={4}>
      <Paper sx={{ p: 3 }}>
        {/* Blog post title/content edit for admin and blog roles */}
        {user && (user.role === "admin" || user.role === "blog") && (
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleEdit("blog", 0)}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteBlog}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={(e) => setInsertMenuAnchor(e.currentTarget)}
            >
              Insert
            </Button>
            <Menu
              anchorEl={insertMenuAnchor}
              open={Boolean(insertMenuAnchor)}
              onClose={() => setInsertMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setInsertType("photos");
                  setInsertMenuAnchor(null);
                  setInsertIndex(null);
                }}
              >
                Photo
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setInsertType("videos");
                  setInsertMenuAnchor(null);
                  setInsertIndex(null);
                }}
              >
                Video
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setInsertType("pdfs");
                  setInsertMenuAnchor(null);
                  setInsertIndex(null);
                }}
              >
                PDF
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setInsertType("audios");
                  setInsertMenuAnchor(null);
                  setInsertIndex(null);
                }}
              >
                Audio
              </MenuItem>
            </Menu>
          </Box>
        )}
        {editing.type === "blog" ? (
          <Box sx={{ mb: 2 }}>
            <input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              style={{ marginRight: 8, width: 300 }}
              placeholder="Title"
            />
            <input
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={{ marginRight: 8, width: 400 }}
              placeholder="Content"
            />
            <Button
              onClick={handleSaveBlog}
              variant="contained"
              color="primary"
              disabled={saving}
            >
              Save
            </Button>
            <Button onClick={handleCancel} variant="outlined" disabled={saving}>
              Cancel
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="h4" gutterBottom>
              {post.title}
            </Typography>
            <Box sx={{ mb: 2 }}>
              {renderContentWithMedia(post.content, post.media)}
              {/* Display all media items if no media markers are found in content */}
              {post.media.length > 0 && !post.content.includes("[media:") && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Media ({post.media.length} items)
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(4, 1fr)",
                      },
                      gap: 2,
                    }}
                  >
                    {post.media.map((item, idx) => (
                      <Box key={idx}>{renderMedia(item, idx)}</Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
        <Divider sx={{ my: 2 }} />
        {/* Comments */}
        <CommentForm postId={post?.id || ""} onSuccess={() => {}} />
        <CommentList postId={post?.id || ""} />
      </Paper>
      {user && (user.role === "admin" || user.role === "blog") && (
        <Dialog open={!!insertType} onClose={() => setInsertType(null)}>
          <DialogTitle>
            Insert {insertType?.slice(0, -1).toUpperCase()}
          </DialogTitle>
          <DialogContent>
            <input
              type="file"
              name="file"
              accept={
                insertType === "photos"
                  ? "image/*"
                  : insertType === "videos"
                  ? "video/*"
                  : insertType === "pdfs"
                  ? ".pdf"
                  : insertType === "audios"
                  ? "audio/*"
                  : undefined
              }
              onChange={handleInsertFieldChange}
              style={{ marginBottom: 12 }}
            />
            <TextField
              margin="dense"
              label="URL (optional if uploading)"
              name="url"
              fullWidth
              value={insertFields.url}
              onChange={handleInsertFieldChange}
            />
            <TextField
              margin="dense"
              label="Heading"
              name="heading"
              fullWidth
              value={insertFields.heading}
              onChange={handleInsertFieldChange}
            />
            {insertType === "audios" && (
              <TextField
                margin="dense"
                label="Transcript"
                name="transcript"
                fullWidth
                value={insertFields.transcript}
                onChange={handleInsertFieldChange}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInsertType(null)}>Cancel</Button>
            <Button onClick={handleInsertSubmit} variant="contained">
              Insert
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogDetail;
